import update from 'immutability-helper'
import * as React from 'react'
import { AddressFormFields } from './Address/AddressForm'
import {
  ITokenizableCard,
  loadAcceptJsScript,
  parseTokenResponse,
  removeAcceptJsScript,
  tokenizeCard,
} from './lib/accept_js'
import * as API from './lib/api'
import { Checkout } from './lib/checkout'
import { PurchaseOption } from './lib/purchase_option'
import { TaxInformation } from './lib/tax_information'

// The shared spine of every membership checkout form (control, redesign, and the
// Stripe `TwoYearCheckout` / `ThreeYearCheckout` subclasses). Owns the parts that
// are payment-critical and identical across variants: Accept.js script lifecycle,
// card tokenization, sales-tax recalculation, and cashier/billing-address plumbing.

export interface IBaseCheckoutProps {
  checkout: API.IMembershipCheckout
  authNet?: API.IAuthorizeNet
}

export interface IBaseCheckoutState {
  checkout: Checkout
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  taxInformation: TaxInformation
  acceptJsId?: string
  paymentToken?: API.IPaymentToken
  paymentTokenErrors?: API.IAuthErrors[]
}

// Build the slice of state shared by every checkout form: the hydrated
// `Checkout`, the default purchase option, and placeholder tax / token fields.
// Subclasses spread this into their own (possibly larger) state in the
// constructor. An empty billing country defaults to 'USA', matching the legacy
// per-component constructors this replaces.
export function buildBaseCheckoutState(props: IBaseCheckoutProps): IBaseCheckoutState {
  const checkout = new Checkout(props.checkout)
  const { billing_address: { country } } = checkout.cashier

  const purchaseOption =
    checkout.purchaseOptions.find((o) => o.name == checkout.defaultPurchaseOption) ||
    checkout.purchaseOptions[0]

  if (!purchaseOption) {
    throw new Error('No purchase option available!')
  }

  if (!country) {
    checkout.cashier = update(checkout.cashier, { billing_address: { country: { $set: 'USA' } } })
  }

  return {
    checkout,
    selectedPurchaseOptionName: purchaseOption.name,
    taxInformation: TaxInformation.placeholder,
    acceptJsId: undefined,
    paymentToken: undefined,
    paymentTokenErrors: undefined,
  }
}

export abstract class AbstractMembershipCheckoutForm<
  P extends IBaseCheckoutProps,
  S extends IBaseCheckoutState
> extends React.Component<P, S> {
  public componentDidMount() {
    const {
      signedIn,
      trial,
      cashier: { billing_address: { country } },
    } = this.state.checkout

    if (signedIn && trial) {
      this.billingAddressChanged(AddressFormFields.Country, country)
    }
    this.updateTaxInformation()
    this.loadAcceptJs()
  }

  public componentWillUnmount() {
    this.unloadAcceptJs()
  }

  public billingAddressChanged = (field: keyof API.IAddress, value: string) => {
    const { checkout: { cashier } } = this.state
    const updatedCashier = update(cashier, { billing_address: { [field]: { $set: value } } })

    this.updateCashier(updatedCashier, () => this.updateTaxInformation())
  }

  protected updateCashier(updatedCashier: API.ICashier, callback?: () => void) {
    const { checkout } = this.state

    this.setState(
      {
        checkout: {
          ...checkout,
          cashier: updatedCashier,
        },
      } as Pick<S, keyof S>,
      callback,
    )
  }

  protected async updateTaxInformation() {
    const { checkout } = this.state
    const { cashier } = checkout
    const {
      billing_address: billingAddress,
      default_billing_address: defaultBillingAddress,
      use_default_billing_address: useDefaultBillingAddress,
      customer: { tax_exempt },
    } = cashier

    if (tax_exempt) {
      this.setState({ taxInformation: TaxInformation.no_tax } as Pick<S, keyof S>)
      return
    } else {
      this.setState({ taxInformation: TaxInformation.placeholder } as Pick<S, keyof S>)
    }

    const address = useDefaultBillingAddress ? defaultBillingAddress : billingAddress

    this.setState({
      taxInformation: await TaxInformation.calculateSalesTax({
        address,
        amount: this.selectedPurchaseOption().actualPrice(),
      }),
    } as Pick<S, keyof S>)
  }

  protected selectedPurchaseOption(): PurchaseOption {
    const {
      selectedPurchaseOptionName,
      checkout: { purchaseOptions },
    } = this.state
    const option = purchaseOptions.find((o) => o.name == selectedPurchaseOptionName)

    if (!option) {
      throw new Error('Bad selected PurchaseOption!')
    }

    return option
  }

  protected loadAcceptJs() {
    // Narrow via a concrete-typed local: TS can't strip `undefined` from the
    // generic indexed access `this.props.authNet` (P['authNet']) across the guard.
    const authNet: API.IAuthorizeNet | undefined = this.props.authNet
    if (!authNet) { return }
    if (this.state.acceptJsId) { return }

    // Only the load event records the id (matching the legacy behavior): until
    // the script is ready there is nothing to unload.
    loadAcceptJsScript(authNet, (acceptJsId) =>
      this.setState({ acceptJsId } as Pick<S, keyof S>),
    )
  }

  protected unloadAcceptJs() {
    if (!this.props.authNet) { return }

    const acceptJsId: string | undefined = this.state.acceptJsId
    if (acceptJsId) {
      removeAcceptJsScript(acceptJsId)
    }
  }

  protected onCheckoutFormSubmit = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    const form = (event.currentTarget as HTMLButtonElement).form

    if (form === null) {
      throw new ReferenceError('button does not belong to form!')
    }

    // The redesign variant can mount the submit button before its payment-step
    // fields exist; the control form always renders them. Guard so an early
    // submit is a clean no-op instead of dereferencing absent fields. In the
    // control form `cashier_payment_type` is always present, so this never
    // short-circuits there.
    const paymentTypeField = form.elements['cashier_payment_type'] as HTMLInputElement | undefined
    if (!form.elements['cashier_payment_profile_id'] && !paymentTypeField) {
      return
    }

    if (form.elements['cashier_payment_profile_id']) {
      form.submit()
    }

    const fieldVal = (resource: string, fieldName: string): string => {
      return form.elements[`cashier_${resource}_${fieldName}`].value
    }

    if (paymentTypeField && paymentTypeField.value === 'credit_card') {
      const creditCard = {
        card_number: fieldVal('credit_card', 'card_number'),
        exp_month: fieldVal('credit_card', 'exp_month'),
        exp_year: fieldVal('credit_card', 'exp_year'),
        cvv: fieldVal('credit_card', 'cvv'),
      }

      this.tokenizeWithAcceptJs(creditCard, () => {
        const { paymentTokenErrors } = this.state

        if (paymentTokenErrors && paymentTokenErrors.length == 0) {
          form.submit()
        } else {
          this.setState({ paymentToken: undefined } as Pick<S, keyof S>)
        }
      })
    }
  }

  protected tokenizeWithAcceptJs(creditCard: ITokenizableCard, callback: () => void) {
    const authNet: API.IAuthorizeNet | undefined = this.props.authNet
    if (!authNet) { return }

    tokenizeCard(authNet, creditCard, (response: any) => {
      this.handleTokenResponse(creditCard, response)
      this.forceUpdate(callback)
    })
  }

  protected handleTokenResponse(creditCard: ITokenizableCard, tokenResponse: any) {
    const { paymentToken, paymentTokenErrors } = parseTokenResponse(creditCard, tokenResponse)

    this.setState({ paymentToken, paymentTokenErrors } as Pick<S, keyof S>)

    if (paymentTokenErrors.length > 0) {
      window.scrollTo(0, 0)
    }
  }
}
